import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Download, ArrowRight } from "lucide-react";

/*
 * BrochureSection — Home page brochure section.
 * Left: front cover image only.
 * Right: existing copy + download button (now a working PDF anchor).
 */
export default function BrochureSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.4, once: true });

  return (
    <section ref={ref} data-testid="brochure-section" className="bg-liftile-matte-black py-20 md:py-28 relative overflow-hidden">
      <div className="absolute inset-0 industrial-grid opacity-[0.05] pointer-events-none" />
      <div
        aria-hidden
        className="absolute -left-32 top-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none mix-blend-screen"
        style={{ background: "radial-gradient(circle, rgba(255,107,26,0.10), transparent 65%)" }}
      />

      <div className="container-x grid lg:grid-cols-2 gap-12 lg:gap-16 items-center relative">
        {/* ---------- LEFT: brochure front cover image ---------- */}
        <div className="relative flex justify-center lg:justify-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-[300px] sm:w-[360px] md:w-[440px] lg:w-[500px]"
          >
            <div
              aria-hidden
              className="absolute -bottom-6 left-6 right-6 h-6 rounded-full blur-2xl opacity-60 bg-black"
            />
            <img
              src="/brochure-cover.png"
              alt="LIFTILE Product Catalogue 2026 — front cover"
              loading="lazy"
              className="relative w-full h-auto rounded-sm shadow-[0_25px_70px_-20px_rgba(0,0,0,0.65)]"
            />
          </motion.div>
        </div>

        {/* ---------- RIGHT: content ---------- */}
        <div>
          <div className="heading-eyebrow mb-3">Product Brochure</div>
          <h2 className="display-2 text-liftile-cream">
            Download Our <span className="text-liftile-orange">2026 Catalog</span>
          </h2>
          <p className="font-body text-liftile-cream/70 mt-5 max-w-xl text-base md:text-lg">
            Complete technical specifications, capacities, and dimensions for the LIFTILE machinery range — engineered, tested and proven on India's biggest construction sites.
          </p>

          <div className="grid grid-cols-3 gap-4 mt-9 max-w-md">
            {[
              { n: "9", l: "Product Lines" },
              { n: "12", l: "Pages" },
              { n: "PDF", l: "Format" },
            ].map((s) => (
              <div key={s.l} className="border-l-2 border-liftile-orange pl-3">
                <div className="font-display text-2xl text-liftile-orange leading-none">{s.n}</div>
                <div className="text-[10px] uppercase tracking-wider text-liftile-cream/60 mt-1">{s.l}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 mt-9">
            <a
              href="/liftile-brochure.pdf"
              download="LIFTILE-Product-Catalogue.pdf"
              data-testid="brochure-download-btn"
              className="group relative inline-flex items-center gap-3 bg-liftile-orange text-white font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-7 py-4 rounded-sm transition-all hover:bg-liftile-orange-dark hover:-translate-y-0.5 hover:shadow-[0_15px_50px_-10px_rgba(255,107,26,0.85)]"
            >
              <Download className="w-4 h-4" />
              <span>Download Brochure</span>
            </a>
            <a
              href="mailto:info@liftile.com?subject=Request%20Catalog"
              className="inline-flex items-center gap-3 border border-white/30 text-liftile-cream font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-7 py-4 rounded-sm transition-all hover:bg-white hover:text-liftile-black"
            >
              Email Me
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
